import { ReactNode, useEffect, useMemo, useState } from 'react';
import Tabs from 'src/components/Tabs';
import { variables } from 'src/Tenants';
import SuiteAssets from 'src/pages/Suite/SuiteAssets';
import SuiteServiceRequests from 'src/pages/Suite/SuiteServiceRequests';
import SuiteMessages from 'src/pages/Suite/SuiteMessages';
import { PATHS } from 'src/common/constants';
import SuiteDocuments from 'src/pages/Suite/SuiteDocuments';
import SuiteMaintainanceGuide from 'src/pages/Suite/SuiteMaintainanceGuide';
import SuiteFaqs from 'src/pages/Suite/SuiteFaqs';
import SuiteWalkThroughs from 'src/pages/Suite/SuiteWalkThroughs';
import { SuiteSpark } from 'src/pages/Suite/SuiteSpark';
import {
  ISparkDeposit,
  IContractFile,
} from '../../../../public/APIContracts/APIContracts';
import { getSparkDeposits, getContractFiles } from 'src/services/client';
import { ModulePermission } from 'src/components/ContactForm/types';
import SuiteNotes from 'src/pages/Suite/SuiteNotes/SuiteNotes';
import { getTenantName } from 'src/helpers/validationHelper';
import SuiteUsers from 'src/pages/Suite/SuiteUsers/SuiteUsers';

interface SuiteTabNavigationProps {
  walkThroughs: any[];
  userRole: string;
  activeTabIndex: number;
  isHomeOwnerOccupantUser: boolean;
  suite: any;
  property: any;
  propertyManagerDetails: any;
  history: any;
  counts: any;
  branding: any;
  match: any;
  isEventShow: boolean;
  noteVisibleToHomeownerOccupantRealtor: boolean;
  onFetchSuiteData: () => void;
  properties: any;
  allowedTabList: any;
}

// Base interface for all tab items
interface BaseTabItem {
  label: string;
  count: string;
  content: ReactNode;
}

interface AdminTabItem extends BaseTabItem {
  localLabel: string;
}

type TabItem = BaseTabItem | AdminTabItem;

const SuiteTabs = ({
  walkThroughs,
  userRole,
  activeTabIndex,
  isHomeOwnerOccupantUser,
  suite,
  property,
  propertyManagerDetails,
  history,
  counts,
  branding,
  match,
  isEventShow,
  noteVisibleToHomeownerOccupantRealtor,
  onFetchSuiteData,
  properties,
  allowedTabList,
}: SuiteTabNavigationProps) => {
  if (!walkThroughs) return null;

  const tenantName = getTenantName();

  const [sparkDeposits, setSparkDeposits] = useState<ISparkDeposit[]>();
  const [sparkDepositsCount, setSparkDepositsCount] = useState<number>(0);
  const [contractFiles, setContractFiles] = useState<IContractFile[]>();
  const [sparkContractsCount, setSparkContractsCount] = useState<number>(0);

  const recordSparkDepositsCount = (newDepositCount: number) => {
    setSparkDepositsCount(newDepositCount);
  };

  const recordSparkContractsCount = (newContractsCount: number) => {
    setSparkContractsCount(newContractsCount);
  };

  const calculateAggregatedDocCount = (
    sparkContractsCount: number,
    documentsCount: number
  ) => {
    const totalCount: number = sparkContractsCount + documentsCount;
    return totalCount.toString();
  };

  // Fetch contract files in parent component
  useEffect(() => {
    const fetchContractFiles = async () => {
      if (!suite.isSpark || !suite?.id) {
        return;
      }

      try {
        const results: IContractFile[] = await getContractFiles(suite.id);
        recordSparkContractsCount(results.length);
        setContractFiles(results);
      } catch (error) {
        console.error(
          '💥 Unexpected exception encountered while fetching spark contract files:',
          error
        );
      }
    };

    fetchContractFiles();
  }, [suite?.id, suite.isSpark]);

  useEffect(() => {
    const fetchSparkDeposits = async () => {
      if (!suite.isSpark || !suite?.id) {
        return;
      }

      try {
        const results: ISparkDeposit[] = await getSparkDeposits(suite.id);

        if (results.length > 0) {
          recordSparkDepositsCount(results.length);
          setSparkDeposits(results);
        }
      } catch (error) {
        console.error(
          '💥 Unexpected exception encountered while fetching spark deposits:',
          error
        );
      }
    };

    fetchSparkDeposits();
  }, [suite?.id]);

  // Memoized base tab items for all user types.
  const baseTabItems: TabItem[] = useMemo(() => {
    const items: TabItem[] = [
      {
        label: variables.roomsAndItems,
        count: counts.assetsCount?.toString() || '0',
        content: (
          <SuiteAssets suite={suite} property={property} history={history} />
        ),
      },
      {
        label: branding.text.serviceRequest + 's',
        count: counts.serviceRequestsCount?.toString() || '0',
        content: <SuiteServiceRequests suite={suite} />,
      },
      {
        label: 'Messages',
        count: counts.messagesCount?.toString() || '0',
        content: (
          <SuiteMessages
            suite={suite}
            history={history}
            property={property}
            url={PATHS.SUITES_VIEW.replace(':id', match.params.id)}
          />
        ),
      },
      {
        label: 'Manuals & Documents',
        count: counts.documentsCount
          ? calculateAggregatedDocCount(
              sparkContractsCount,
              counts.documentsCount
            )
          : sparkContractsCount.toString(),
        content: (
          <SuiteDocuments
            suite={suite}
            properties={properties}
            contractFiles={contractFiles}
          />
        ),
      },
      {
        label: 'Maintenance Reminders',
        count: counts.maintainanceGuideCount?.toString() || '0',
        content: <SuiteMaintainanceGuide suite={suite} />,
      },
      {
        label: 'People',
        count: counts.peopleCount?.toString() || '0',
        content: (
          <SuiteUsers
            suite={suite}
            property={property}
            onFetchSuiteData={onFetchSuiteData}
          />
        ),
      },
      {
        label: 'FAQs',
        count: counts.faqsCount?.toString() || '0',
        content: <SuiteFaqs suite={suite} />,
      },
    ];

    // Add deposits tab if applicable
    if (suite.isSpark && sparkDepositsCount > 0) {
      items.push({
        label: 'Deposits',
        count: sparkDepositsCount.toString(),
        content: <SuiteSpark suite={suite} sparkDeposits={sparkDeposits} />,
      });
    }

    return items;
  }, [
    suite,
    property,
    history,
    counts,
    branding,
    match,
    properties,
    onFetchSuiteData,
    contractFiles,
    sparkContractsCount,
    sparkDeposits,
    sparkDepositsCount,
  ]);

  // Memoized admin tab items.
  const adminTabItems: TabItem[] = useMemo(() => {
    const items: TabItem[] = [
      {
        localLabel: 'Rooms and Items',
        label: variables.roomsAndItems,
        count: counts.assetsCount ? counts.assetsCount.toString() : '0',
        content: (
          <SuiteAssets suite={suite} property={property} history={history} />
        ),
      },
      {
        localLabel: branding.text.serviceRequest + 's',
        label: branding.text.serviceRequest + 's',
        count: counts.serviceRequestsCount?.toString() || '0',
        content: <SuiteServiceRequests suite={suite} />,
      },
      {
        localLabel: 'Manuals & Documents',
        label: 'Manuals & Documents',
        count: counts.documentsCount
          ? calculateAggregatedDocCount(
              sparkContractsCount,
              counts.documentsCount
            )
          : sparkContractsCount.toString(),
        content: (
          <SuiteDocuments
            suite={suite}
            properties={properties}
            contractFiles={contractFiles}
          />
        ),
      },
      {
        localLabel: 'Messages & Announcements',
        label: 'Messages',
        count: counts.messagesCount?.toString() || '0',
        content: (
          <SuiteMessages
            suite={suite}
            history={history}
            property={property}
            url={PATHS.SUITES_VIEW.replace(':id', match.params.id)}
          />
        ),
      },
      {
        localLabel: 'Maintenance Reminders',
        label: 'Maintenance Reminders',
        count: counts.maintainanceGuideCount?.toString() || '0',
        content: <SuiteMaintainanceGuide suite={suite} />,
      },
      {
        localLabel: 'People',
        label: 'People',
        count: counts.peopleCount?.toString() || '0',
        content: (
          <SuiteUsers
            suite={suite}
            property={property}
            onFetchSuiteData={onFetchSuiteData}
          />
        ),
      },
      {
        localLabel: 'FAQs',
        label: 'FAQs',
        count: counts.faqsCount?.toString() || '0',
        content: <SuiteFaqs suite={suite} />,
      },
      {
        localLabel: 'Notes',
        label: 'Notes',
        count: counts.notesCount?.toString() || '0',
        content: (
          <SuiteNotes suite={suite} onFetchSuiteData={onFetchSuiteData} />
        ),
      },
      {
        localLabel: 'WalkThrough',
        label: 'WalkThrough',
        count: counts.walkthroughsCount?.toString() || '0',
        content: (
          <SuiteWalkThroughs
            suite={suite}
            property={property}
            isEventShow={isEventShow}
            propertyManagerDetails={propertyManagerDetails} // Add this line
          />
        ),
      },
    ];

    // Add deposits tab if applicable.
    if (suite.isSpark && sparkDepositsCount > 0) {
      items.push({
        localLabel: 'Spark Deposits',
        label: 'Deposits',
        count: sparkDepositsCount.toString(),
        content: <SuiteSpark suite={suite} sparkDeposits={sparkDeposits} />,
      });
    }

    return items;
  }, [
    suite,
    property,
    history,
    counts,
    branding,
    match,
    properties,
    isEventShow,
    onFetchSuiteData,
    contractFiles,
    sparkContractsCount,
    sparkDeposits,
    sparkDepositsCount,
  ]);

  // Memoized notes tab.
  const notesTab: TabItem = useMemo(() => {
    const shouldShowNotes: boolean = !(
      isHomeOwnerOccupantUser && !noteVisibleToHomeownerOccupantRealtor
    );

    const newTabItem: TabItem = {
      label: 'Notes',
      count: counts.notesCount?.toString() || '0',
      content: <SuiteNotes suite={suite} onFetchSuiteData={onFetchSuiteData} />,
    };

    return shouldShowNotes ? newTabItem : null;
  }, [
    isHomeOwnerOccupantUser,
    noteVisibleToHomeownerOccupantRealtor,
    counts,
    suite,
  ]);

  // Memoized walkthrough tab.
  const walkThroughTab: BaseTabItem = useMemo(() => {
    const newWalkThroughTab: BaseTabItem = {
      label: 'WalkThrough',
      count: counts.walkthroughsCount?.toString() || '0',
      content: (
        <SuiteWalkThroughs
          suite={suite}
          property={property}
          isEventShow={isEventShow}
          propertyManagerDetails={propertyManagerDetails} // Add this line
        />
      ),
    };

    return newWalkThroughTab;
  }, [suite, property, isEventShow, counts]);

  // Check for active walkthroughs.
  const hasActiveWalkthroughs: boolean = useMemo(() => {
    return (
      walkThroughs.some((walkThroughItem: any) => {
        if (walkThroughItem.walkthroughType === 'Home Owner Walk-through') {
          const activeStatuses = [
            'Guided Walk Through',
            'Self-Guided Walk Through',
            'In Progress Home Only',
            'In Progress Home',
            'In Progress',
            'Begin',
            'Schedule',
          ];
          return activeStatuses.includes(walkThroughItem.status);
        }

        return false;
      }) && walkThroughs.length > 0
    );
  }, [walkThroughs]);

  // Memoized occupant tabs.
  const occupantTabs: BaseTabItem[] = useMemo(() => {
    if (hasActiveWalkthroughs) {
      return [walkThroughTab, ...baseTabItems, ...(notesTab ? [notesTab] : [])];
    }

    return [...baseTabItems, ...(notesTab ? [notesTab] : []), walkThroughTab];
  }, [baseTabItems, notesTab, walkThroughTab, hasActiveWalkthroughs]);

  const defaultTabs: BaseTabItem[] = useMemo(() => {
    return [...baseTabItems, ...(notesTab ? [notesTab] : [])];
  }, [baseTabItems, notesTab]);

  // Todo: Resolve root for this hack for GableCraft from API - https://app.asana.com/1/41213529744312/project/1208712340140532/task/1211337293763788?focus=true
  const handleGableCraftServiceRequestTab = (
    allowedItemName: string,
    tabLabel: string
  ) => {
    const isGableCraftTenant: boolean =
      tenantName === 'GableCraftMF' || tenantName === 'GableCraft';

    const isValidServiceRequestMapping: boolean =
      allowedItemName === 'Service Requests' &&
      tabLabel === 'Warrantable Requests';

    return isGableCraftTenant && isValidServiceRequestMapping;
  };

  const getFilteredTabsForHomeOwner = (
    tabItems: TabItem[],
    allowedTabList: ModulePermission[]
  ): any[] => {
    if (!allowedTabList?.length) {
      return allowedTabList;
    }

    const matchingTabs: (TabItem | undefined)[] = allowedTabList.map(
      (allowedItem: ModulePermission) => {
        const foundTab: TabItem = tabItems.find((tab: TabItem) => {
          const isMatch: boolean =
            tab.label === allowedItem.name ||
            handleGableCraftServiceRequestTab(allowedItem.name, tab.label);

          return isMatch;
        });

        return foundTab;
      }
    );

    const validTabs: TabItem[] = [];

    for (const tab of matchingTabs) {
      if (tab !== undefined) {
        validTabs.push(tab);
      }
    }

    return validTabs;
  };

  const tabItems: BaseTabItem[] = useMemo(() => {
    const OCCUPANT_ROLES: string[] = ['Home Owner', 'Realtor', 'Occupant'];
    const ADMIN_ROLES: string[] = [
      'Property Developer',
      'Admin',
      'Super Admin',
    ];

    if (OCCUPANT_ROLES.includes(userRole)) {
      return getFilteredTabsForHomeOwner(occupantTabs, allowedTabList);
    }

    if (ADMIN_ROLES.includes(userRole)) {
      return adminTabItems;
    }

    return defaultTabs;
  }, [userRole, occupantTabs, adminTabItems, defaultTabs]);

  return <Tabs from="suite" activeTab={activeTabIndex} items={tabItems} />;
};

export default SuiteTabs;
